import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Tabs, Tab, Typography, Button, IconButton, Snackbar, CircularProgress } from "@material-ui/core";
import "./exam-review-drawer.scss";
import {
  getQuesFeedback,
  getQuesTutorFeedback,
  userDetailsFromToken,
  resetDemoQuestionMetadata,
  resetDemoQuestionIDs,
} from "../../actions";
import { Forum, Launch, Check, Clear } from "@material-ui/icons";
import QuestionFeedback from '../../components/exam-header/question-feedback';
import DetailedGuidanceContent from '../../components/detailed-guidance-content/detailed-guidance-content';
import PhoneAuthSection from '../login/PhoneAuthSection';
import url from "../../constants/urlList";
import { api } from "../../services/apiService";
import { buildQLinkOAuthState } from "../../utils/attribution";
import { clearGoogleSameTabSignIn, markGoogleSameTabSignIn } from "../../utils/google-same-tab-signin";
import { GOOGLE_POPUP_SIGNED_IN } from "../../utils/google-popup-result";
import { getActiveReferralCode, getReferralCapturedAtForSignup } from "../../utils/referral";
import { resolveInitialTheme } from "../../utils/theme-preference";
import { trackExplanationOpened } from "../../utils/diagnostic-events";
import { trackUserSignIn, trackAdConversion } from "../../utils/tracking";
import { detectInAppBrowser } from "../../utils/in-app-browser";
import { buildQlinkPracticeState } from "../../utils/qlink-exam-seed";
import { FREE_ALLOWANCE_EXAM, fetchFreeExamAllowance, startPracticingLabel } from "../../utils/qlink-free-allowance";
import { FREE_DAILY_ABSITE_QUESTIONS } from "../../constants/freeAllowance";
import InAppEscapePanel from "./inapp-escape-panel";
import QLinkSigninTeaser, { TEASER_KIND_PROMPT, trackTeaserAuthStart } from "./qlink-signin-teaser";
import {
  QLINK_FUNNEL_EVENT,
  inAppFunnelProps,
  markQLinkSignInThisVisit,
  recordQLinkFunnelEvent,
  signinDimension,
} from "../../utils/qlink-funnel-events";
import { REPLAY_MASK } from "../../utils/replay-mask";
// import googleLogo from "../../images/google-icon.png";
const googleLogo = require("../../images/google-icon.png").default;

// Loaded only when the reference-PDF panel opens (isPdf): keeps react-pdf and
// pdfjs out of the bundle every drawer page downloads, including the public
// shared-question landing.
const DrawerPdfViewer = lazy(() => import("./drawer-pdf-viewer"));

let guideWindow = null;

const NOT_IN_APP = { inApp: false, app: null, os: 'other' };

// Google popup outcome for the first-party funnel (qlink-funnel-events): poll
// popup.closed once a second; after a close, wait for a late success message
// before calling the attempt cancelled; stop polling after 10 minutes.
const GOOGLE_POPUP_POLL_MS = 1000;
const GOOGLE_POPUP_LATE_SUCCESS_MS = 2000;
const GOOGLE_POPUP_MAX_POLLS = 600;
const GOOGLE_FUNNEL_SURFACES = ['teaser', 'wall', 'inapp_escape'];

const ExamReviewDrawer = (props) => {
  const { questionData, openDrawer, quizHistory, onPdfClose, isOpenPdf, pdfData, onFeedbackOpen,
    readOnly = false, isTutor, isGuide, isReview, onGuideOpen, currentQuestionNum, isUserAcquisition, isUserLoggedIn, selectedAnswer = '', handleFetchQuestionMetadata = () => { }, questionMetadataError, isQuestionMetadataLoading = false, qlinkId, qlinkPlatform,
    // R3 (P0 blank-explanation fix): an answered question whose explanation
    // never arrived (A2 grade failed, metadata errored, or the reveal was
    // empty) must render an explicit unavailable/retry state — never a silent
    // blank. serverGradeError carries the A2 failure; isAnswered says the
    // question is graded/answered (so the unavailable state applies); the strip
    // for UNanswered questions is unchanged.
    serverGradeError, isAnswered = false, onRetryExplanation, drawerAutoOpened,
    answeredCorrectly: answeredCorrectlyProp, hasAnswered,
    // Signed-out QLink answer check result from the container:
    // { status: 'pending'|'done'|'error', correct, correctAnswer, correctLetter }.
    publicAnswerResult,
    // Single exam named by the question's categories, or "" (generic copy).
    qlinkExamLabel = '',
    // Signed-out QLink only: the public question's id, for the sign-in funnel
    // and teaser when questionData carries none (after a revoked login is
    // cleared in place no public metadata is fetched). Deliberately NOT merged
    // into questionData: the admin author fetch and the feedback refresh key
    // off questionData's id.
    publicQuestionId,
    // "‹ Question" in the curated-teaser sheet (container closes the drawer).
    onBackToQuestion,
    // Phone sheet covering the page (dialog semantics), and whether opening
    // may move focus (not for arrow-key answers on the side drawer).
    teaserModal = false, teaserMoveFocus = true } = props;
  const [selectedTab, setSelectedTab] = useState('explanation');
  const [isPdf, setIsPdf] = useState(false);
  const [guideData, setGuideData] = useState({});
  const [pageNumber, setPageNumber] = useState(10);
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(600);
  const [searchText, setSearchText] = useState('');
  const [isTutorRole, setIsTutorRole] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Admin-only author byline. The email is no longer in the question payload
  // (QLearn-service #484 stripped it); fetch it per question from the
  // admin-guarded author endpoint so admins keep attribution and students
  // never receive it.
  const [authorEmail, setAuthorEmail] = useState("");
  const [quesFeedbackArr, setQuesFeedbackArr] = useState([]);
  const [quesTutorFeedbackArr, setQuesTutorFeedbackArr] = useState([]);
  const [loginFailed, setLoginFailed] = useState(false);
  const [authMethod, setAuthMethod] = useState('google'); // 'google' or 'phone'
  // Social in-app browser (CONTRACT-FIX1): Google refuses OAuth there, so the
  // signed-out wall defaults to Phone and the Google tab offers an escape to a
  // real browser. Detected once; real browsers keep today's behaviour.
  const [inAppBrowser] = useState(() => detectInAppBrowser() || NOT_IN_APP);
  const [authTabPicked, setAuthTabPicked] = useState(false);

  const pageRef = useRef(null);
  // Set when THIS drawer's Google popup hands back a session; the sign-up
  // conversion fires once the user details resolve (mirrors login.jsx).
  const popupSignInPendingRef = useRef(false);
  const loginListenerRef = useRef(null);
  // Google popup attempts: { popup, funnel, record, outcome, interval, grace }. Each
  // attempt records exactly one funnel outcome (the first wins): 'success' on
  // its Login Successful message, 'cancelled' when its popup closed with no
  // success after a grace period for a late message. A superseded attempt
  // (its still-open window reused by a retry) records nothing.
  const googleAttemptsRef = useRef([]);
  const stopAttemptWatch = (attempt) => {
    clearInterval(attempt.interval);
    clearTimeout(attempt.grace);
    attempt.interval = null;
    attempt.grace = null;
  };
  const clearLoginListener = () => {
    googleAttemptsRef.current.forEach(stopAttemptWatch);
    googleAttemptsRef.current = [];
    if (loginListenerRef.current) {
      window.removeEventListener("message", loginListenerRef.current);
    }
    loginListenerRef.current = null;
  };
  const history = useHistory();
  const dispatch = useDispatch();

  const quesFeedback = useSelector((state) => {
    return state.getQuesFeedback;
  });

  const setQuesFeedbackRes = useSelector((state) => {
    return state.setQuesFeedback;
  });

  const quesTutorFeedback = useSelector((state) => {
    return state.getQuesTutorFeedback;
  });

  // B2 (REVIEW-SCOPE): resolve the theme the SAME way exam-header does — the
  // latest toggle (setUserPreference) wins over the loaded record
  // (getUserPreference). Subscribing to setUserPreference is what makes the
  // tab-label dark class re-render on an in-session theme toggle (the drawer
  // previously read getUserPreference only, which does not update on toggle).
  const userPreferenceData = useSelector((state) => {
    return state.setUserPreference?.preference || state.getUserPreference?.preference;
  });
  // Drive the dark-only tab-label class off the RESOLVED theme, not the raw
  // darkTheme field: resolveInitialTheme falls back to prefers-color-scheme when
  // no saved preference exists, so OS-defaulted-dark users (the cohort S6 newly
  // serves dark) get the dark token routing and avoid the ~1.3-1.8:1 C4 tab
  // fallback. It stays correct after an in-session toggle because a saved boolean
  // (now surfaced via setUserPreference above) short-circuits the OS fallback.
  const isDarkTheme = resolveInitialTheme(userPreferenceData) === 'dark';


  useEffect(() => {
    if (isGuide) {
      const channel = new BroadcastChannel('app-data');
      channel.addEventListener('message', (event) => {
        setGuideData(event.data);
      });
    }
    const role = window.localStorage.getItem('role');
    setIsTutorRole(role === 'ROLE_TUTOR');
    setIsAdmin(role === 'ROLE_ADMIN');

    return () => {
      clearLoginListener();
      if (guideWindow) {
        guideWindow.close();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (questionData) {
      setGuideData(questionData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);


  useEffect(() => {
    if (!readOnly && setQuesFeedbackRes?.quesFeedback && setQuesFeedbackRes?.loading === false) {
      const questionId = questionData?.id;
      if (questionId) {
        dispatch(getQuesFeedback(questionId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setQuesFeedbackRes, readOnly]);

  useEffect(() => {
    if (quesFeedback?.loading !== undefined && !quesFeedback?.loading) {
      setQuesFeedbackArr(quesFeedback.quesFeedback || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quesFeedback]);

  useEffect(() => {
    if (quesTutorFeedback?.loading !== undefined && !quesTutorFeedback?.loading) {
      setQuesTutorFeedbackArr(quesTutorFeedback.quesTutorFeedback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quesTutorFeedback]);

  useEffect(() => {
    setPageWidth(pageRef?.current?.offsetWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRef.current]);

  useEffect(() => {
    if (pdfData) {
      setPageNumber(pdfData.page);
      setSearchText(pdfData.text);
    }
  }, [pdfData]);

  useEffect(() => {
    const questionId = guideData?.id;
    if (questionId && !isUserAcquisition && !readOnly) {
      if (isTutorRole) {
        dispatch(getQuesTutorFeedback(questionId));
      } else {
        dispatch(getQuesFeedback(questionId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, guideData, readOnly]);

  // Admin author byline: fetch per question. Non-admins never call it (and the
  // endpoint 403s them anyway); a failed/absent response just clears the line.
  useEffect(() => {
    const questionId = guideData?.id;
    if (!isAdmin || !questionId || readOnly) {
      setAuthorEmail("");
      return;
    }
    // Clear immediately so navigating between questions never shows the
    // previous question's author while this fetch is in flight.
    setAuthorEmail("");
    let cancelled = false;
    api.getQuestionAuthor(questionId).then((res) => {
      if (!cancelled) {
        setAuthorEmail(res && !res.status && res.userEmail ? res.userEmail : "");
      }
    }).catch(() => {
      if (!cancelled) {
        setAuthorEmail("");
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, guideData, readOnly]);

  const getPageProps = () => {
    return {
      className: 'pdf-page',
      scale: 1,
      // renderTextLayer,
      width: pageWidth,
      customTextRenderer: (text) => {
        const splitText = text.str.split(searchText);

        if (splitText.length <= 1) {
          return text.str;
        }

        const matches = text.str.match(new RegExp(searchText, 'i'));

        return splitText.reduce((arr, element, index) => (matches[index] ? [
          ...arr,
          element,
          <mark key={index}>
            {matches[index]}
          </mark>,
        ] : [...arr, element]), []);
      }
    };
  }

  const pageProps = getPageProps();

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  }

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const closePdf = () => {
    setIsPdf(false);
    onPdfClose();
  };

  useEffect(() => {
    setIsPdf(isOpenPdf);
  }, [isOpenPdf]);

  const convertUrl = (url) => {
    if (url.includes('youtube')) {
      return url.replace('watch?v=', 'embed/') + '?autoplay=1';
    }

    return url;
  }

  const onGuidePopOut = () => {
    if (readOnly) return;
    if (!guideWindow) {
      let path = window.location.pathname;

      if (isReview) {
        path = path.replace('review', 'tutor/1');
      }

      guideWindow = window.open(path + '/guide/1', '_blank', 'width=800,height=700,left=700,popup');
      onGuideOpen();

      guideWindow.addEventListener('load', () => {
        guideWindow.addEventListener('beforeunload', (e) => {
          // e.preventDefault();
          guideWindow = null;
          // e.returnValue = true;
        });
      });
    } else {
      alert('Guide already open');
    }
  }

  const getTransformedText = (text) => {
    if (text.includes('_')) {
      return text.replace('_', ' ');
    }

    return text;
  }

  const sendToLogin = () => {
    window.location = "/";
  }

  // The question the sign-in funnel and teaser are about. The container's
  // public id comes first: it is the CURRENT question, while questionData can
  // lag one render behind a question change.
  const funnelQuestionId = publicQuestionId ?? questionData?.id ?? questionData?.questionId;

  // First-party funnel events for this QLink (no-op off a QLink). Never
  // awaited, never throws.
  const recordDrawerFunnel = (event, funnelProps) => recordQLinkFunnelEvent({
    qlinkId: isUserAcquisition ? qlinkId : undefined,
    questionId: funnelQuestionId,
    platform: qlinkPlatform || 'unknown',
    event,
    props: funnelProps,
  });

  const loginSuccessListener = (response) => {
    if (response.origin !== window.location.origin) {
      return;
    }
    // Any tracked attempt's popup may sign in: a closed popup's late message
    // still counts during (and, for sign-in only, after) its grace period.
    const attempt = googleAttemptsRef.current.find((a) => a.popup && response.source === a.popup);
    if (!attempt) {
      return;
    }
    const data = response.data;
    const token = data?.results?.accessToken;
    // Anything else, GOOGLE_POPUP_NOT_FINISHED included, is ignored: the popup
    // closes itself and the watch below records the attempt as cancelled.
    if (data?.msg !== GOOGLE_POPUP_SIGNED_IN || typeof token !== "string" || !token.trim()) {
      return;
    }
    const recordOutcome = attempt.outcome === null;
    attempt.outcome = attempt.outcome || 'success';
    clearLoginListener();
    window.localStorage.setItem("token", token);
    markQLinkSignInThisVisit();
    if (recordOutcome) {
      attempt.record(QLINK_FUNNEL_EVENT.AUTH_RESULT, { ...attempt.funnel, result: 'success' });
    }
    popupSignInPendingRef.current = true;
    dispatch(userDetailsFromToken({ door: 'google' }));
    setLoginFailed(false);
  };

  const userDetailsFromTokenRes = useSelector((state) => state.userDetailsFromToken?.userDetails);

  // Sign-up conversion for the drawer's Google path, the same two calls
  // login.jsx makes for Google OAuth (AUDIT fix #3): without them Meta/Google
  // Ads never saw the one sign-in that keeps the shared question.
  useEffect(() => {
    if (!popupSignInPendingRef.current) {
      return;
    }
    if (userDetailsFromTokenRes?.status === 200 || userDetailsFromTokenRes?.id) {
      popupSignInPendingRef.current = false;
      const oauthUserData = {
        id: userDetailsFromTokenRes?.id,
        email: userDetailsFromTokenRes?.email,
        displayName: userDetailsFromTokenRes?.displayName,
        primaryRole: userDetailsFromTokenRes?.primaryRole
      };
      trackUserSignIn(oauthUserData, 'google_oauth');
      trackAdConversion(oauthUserData, 'google_oauth');
    }
  }, [userDetailsFromTokenRes]);

  const isInAppStranger = !!inAppBrowser.inApp && !!isUserAcquisition && !isUserLoggedIn;
  // Phone by default in a webview, decided at render time (so the Google tab
  // and its escape panel never flash, and "shown" counts only real opens).
  // It applies as soon as the page marks the viewer as a QLink stranger, and
  // never overrides a tab the user picked.
  const effectiveAuthMethod = isInAppStranger && !authTabPicked ? 'phone' : authMethod;
  const showInAppEscape = isInAppStranger && effectiveAuthMethod === 'google';

  const isPopupClosed = (popup) => {
    try {
      return !!popup?.closed;
    } catch (e) {
      return false;
    }
  };

  // A closed popup with no success message is a cancelled attempt.
  const watchGooglePopup = (attempt) => {
    let polls = 0;
    attempt.interval = setInterval(() => {
      polls += 1;
      if (isPopupClosed(attempt.popup)) {
        clearInterval(attempt.interval);
        attempt.interval = null;
        attempt.grace = setTimeout(() => {
          attempt.grace = null;
          if (attempt.outcome === null) {
            attempt.outcome = 'cancelled';
            attempt.record(QLINK_FUNNEL_EVENT.AUTH_RESULT, { ...attempt.funnel, result: 'failure', reason: 'cancelled' });
          }
        }, GOOGLE_POPUP_LATE_SUCCESS_MS);
      } else if (polls >= GOOGLE_POPUP_MAX_POLLS) {
        clearInterval(attempt.interval);
        attempt.interval = null;
      }
    }, GOOGLE_POPUP_POLL_MS);
  };

  // Before a retry: an unresolved attempt whose popup already closed (its
  // grace running, or closed since the last poll) keeps its watch, so it still
  // records its own outcome and a late success still signs in. A still-open
  // popup is reused by the retry's window.open, so that attempt is superseded
  // and records nothing; finished attempts are dropped.
  const prepareGoogleRetry = () => {
    googleAttemptsRef.current = googleAttemptsRef.current.filter((attempt) => {
      const stillSettling = attempt.outcome === null
        && (attempt.grace || (attempt.interval && isPopupClosed(attempt.popup)));
      if (!stillSettling) {
        stopAttemptWatch(attempt);
      }
      return stillSettling;
    });
    if (!googleAttemptsRef.current.length && loginListenerRef.current) {
      window.removeEventListener("message", loginListenerRef.current);
      loginListenerRef.current = null;
    }
  };

  // surface: 'teaser' | 'wall' | 'inapp_escape' for the funnel; anything else
  // (e.g. the click event from "Sign in again") records no surface.
  const onGoogleSignUp = (surface) => {
    prepareGoogleRetry();
    setLoginFailed(false);

    let oauthUrl = `${url.domain}/qlearn/oauth2/authorization/google`;

    const params = new URLSearchParams();
    if (isUserAcquisition && qlinkId) {
      const platform = qlinkPlatform || 'unknown';
      const qLinkState = buildQLinkOAuthState({ qlinkId, platform });
      if (qLinkState) {
        params.set('state', qLinkState);
      }
    }
    const referralCode = getActiveReferralCode(window.localStorage);
    if (referralCode) {
      params.set('ref', referralCode);
      const referralCapturedAt = getReferralCapturedAtForSignup(window.localStorage);
      if (referralCapturedAt) params.set('refAt', referralCapturedAt);
    }
    const queryStr = params.toString();
    if (queryStr) oauthUrl += `?${queryStr}`;

    const googleFunnel = {
      method: 'google',
      ...inAppFunnelProps(inAppBrowser),
      ...(GOOGLE_FUNNEL_SURFACES.includes(surface) ? { surface } : {}),
    };
    recordDrawerFunnel(QLINK_FUNNEL_EVENT.AUTH_START, googleFunnel);

    // window.open copies this tab's sessionStorage into the popup, so the
    // popup gets a fresh same-tab mark of its own and this tab keeps none. A
    // popup that loses its opener (this tab closed, or an enforced
    // Cross-Origin-Opener-Policy page in the sign-in) can then still finish
    // the visitor's sign-in on /socialAuth, which stores a token only with a
    // mark (see googleAuthRedirect), and a link opened here later finds none.
    markGoogleSameTabSignIn();
    let popup;
    try {
      popup = window.open(oauthUrl, "Sign In", "height=400,width=600");
    } finally {
      clearGoogleSameTabSignIn();
    }
    if (!popup) {
      recordDrawerFunnel(QLINK_FUNNEL_EVENT.AUTH_RESULT, { ...googleFunnel, result: 'failure', reason: 'popup_blocked' });
      setLoginFailed(true);
      return;
    }
    // The attempt keeps the recorder of the render that started it, so its
    // outcome carries the question it started on even if the drawer moves on.
    const attempt = { popup, funnel: googleFunnel, record: recordDrawerFunnel, outcome: null, interval: null, grace: null };
    googleAttemptsRef.current.push(attempt);
    // One listener for every tracked attempt, re-bound to this render.
    if (loginListenerRef.current) {
      window.removeEventListener("message", loginListenerRef.current);
    }
    loginListenerRef.current = loginSuccessListener;
    window.addEventListener("message", loginSuccessListener, false);
    watchGooglePopup(attempt);
  };

  // Strip-safe acquisition states (C2 flip gate F1): with the in-quiz answer
  // strip on, the server never sends option.correctAnswer to a QLink viewer
  // (no quiz context, so the reveal fail-closes — QLS InQuizRevealService),
  // and a missing key must render NEITHER state. The unguarded `!==` below
  // read `undefined !== answer` as "wrong" and painted every answered
  // question red for logged-in acquisition users. With a non-empty key
  // (today, strip off) both values are unchanged.
  const acquisitionCorrectAnswer = typeof guideData?.option?.correctAnswer === 'string' ? guideData.option.correctAnswer.trim() : '';
  // Signed out: only a server-checked result colors the header (never the
  // payload, which carries no key for anonymous viewers).
  const signedOutResult = isUserAcquisition && !isUserLoggedIn && publicAnswerResult?.status === 'done'
    ? publicAnswerResult
    : null;
  // Once the answer check has answered, the result card: the curated "sign in
  // to see why" teaser when the check returned one for this question, else the
  // same card as a plain sign-in prompt (every daily-post question today).
  // Before an answer, while it is checked, or when the check failed, the wall
  // below stays.
  const teaserResult = signedOutResult;
  const teaserKind = teaserResult?.teaser?.firstSentence ? undefined : TEASER_KIND_PROMPT;
  // Before an answer there is no "why" yet (the drawer can be opened first).
  const signInLead = selectedAnswer
    ? 'Sign in free to see why: the full explanation for'
    : 'Sign in free for the full explanation of';
  const isUserAcquisitionCorrectAns = (isUserLoggedIn && isUserAcquisition && selectedAnswer && !!acquisitionCorrectAnswer && acquisitionCorrectAnswer === selectedAnswer?.trim())
    || signedOutResult?.correct === true;
  const isUserAcquisitionWrongAns = (isUserLoggedIn && isUserAcquisition && selectedAnswer && !!acquisitionCorrectAnswer && acquisitionCorrectAnswer !== selectedAnswer?.trim())
    || signedOutResult?.correct === false;
  const showQuestionMetadataLoading = isQuestionMetadataLoading && !questionMetadataError;
  // R3: student-visible explanation content. The full `explanation` is admin-
  // only (rendered below), so for a student the content is brief/additional;
  // admins also see `explanation`. A held reveal with none of these — or a
  // grade/metadata error — is the silent-blank the P0 came from.
  const hasExplanationContent = !!(guideData?.briefExplanation || guideData?.additionalExplanation || (isAdmin && guideData?.explanation));
  const showExplanationUnavailable = isUserLoggedIn && !isUserAcquisition && !showQuestionMetadataLoading && isAnswered && !hasExplanationContent;
  const explanationErrorCode = serverGradeError && serverGradeError !== true ? serverGradeError : null;

  // QLink same-case guidance states (QLINK-EXPLANATION-CONTRACT-SEPT16): the
  // acquisition metadata error arrives as a status-carrying object
  // (apiService qlinkGuidanceFailure, ridden verbatim through the untouched
  // saga/reducer), and the status picks the ONE actionable state — genuine
  // re-auth for 401 (never an endless Retry against a dead login),
  // reopen-original-link for an unavailable/revoked link, retry for anything
  // transient. All gated to the LOGGED-IN acquisition viewer: the anonymous
  // sign-in wall below stays the gate for everyone else.
  const qlinkGuidanceError = isUserAcquisition
    && questionMetadataError
    && typeof questionMetadataError === "object"
    && questionMetadataError.qlinkGuidance
    ? questionMetadataError
    : null;
  const qlinkGuidanceStatus = qlinkGuidanceError ? Number(qlinkGuidanceError.status) : null;
  const showQlinkSignInAgain = isUserLoggedIn && qlinkGuidanceStatus === 401;
  const showQlinkUnavailable = isUserLoggedIn && qlinkGuidanceStatus === 404;
  const showQlinkRetry = isUserLoggedIn && !!qlinkGuidanceError
    && qlinkGuidanceStatus !== 401 && qlinkGuidanceStatus !== 404;
  // Empty SUCCESSFUL guidance after answering is just as blank as an error —
  // it must be actionable too, not silent.
  const showQlinkEmptyGuidance = isUserLoggedIn && isUserAcquisition && !!selectedAnswer
    && !qlinkGuidanceError && !showQuestionMetadataLoading && !hasExplanationContent;
  const showStartPracticing = isUserLoggedIn && isUserAcquisition && !!selectedAnswer;
  // The free-allowance count on Start practicing comes from the server for
  // THIS account (qlink-free-allowance.js): its selected plan is free and
  // covers the exam, and today's remaining allowance. Until that answers, or
  // when it does not establish a free allowance, the label is plain.
  // Kept with the account it was fetched for, so a different signed-in
  // account never renders another account's count, even for one frame.
  const [freeAllowance, setFreeAllowance] = useState({ userId: null, allowance: null });
  const signedInUserId = userDetailsFromTokenRes?.id;
  const checkFreeAllowance = showStartPracticing && qlinkExamLabel === FREE_ALLOWANCE_EXAM && !!signedInUserId;
  useEffect(() => {
    if (!checkFreeAllowance) {
      return undefined;
    }
    let current = true;
    fetchFreeExamAllowance(signedInUserId).then((allowance) => {
      if (current) {
        setFreeAllowance({ userId: signedInUserId, allowance });
      }
    });
    return () => { current = false; };
  }, [checkFreeAllowance, signedInUserId]);
  const practiceLabel = startPracticingLabel(
    checkFreeAllowance && freeAllowance.userId === signedInUserId ? freeAllowance.allowance : null
  );

  const onStartPracticing = () => {
    // Recorded before navigation, while this is still the QLink page.
    recordDrawerFunnel(QLINK_FUNNEL_EVENT.START_PRACTICING_CLICKED, {
      signin: signinDimension(),
      browser: inAppFunnelProps(inAppBrowser).browser,
    });
    // Mirrors exam.jsx goToHomePage's acquisition exit: navigation plus local
    // acquisition-state cleanup — a leftover userAcqQuesId hijacks question
    // navigation inside the student's next real quiz. Navigation ONLY: no
    // quiz, payment or provider mutation; /create-quiz is where the student
    // builds their own quiz.
    // The question's exam rides along so the new-account exam picker opens
    // with it selected (qlink-exam-seed.js).
    const practiceState = buildQlinkPracticeState(qlinkExamLabel);
    if (practiceState) {
      history.push("/create-quiz", practiceState);
    } else {
      history.push("/create-quiz");
    }
    dispatch(resetDemoQuestionMetadata());
    dispatch(resetDemoQuestionIDs());
    window.localStorage.removeItem("userAcqQuesId");
    window.localStorage.removeItem("userAcquisitiontoken");
  };

  /*
   * Explanation opened — the content-quality proxy. Placed HERE, below
   * hasExplanationContent, because it reads it; the first version sat above and
   * would have hit the temporal dead zone.
   *
   * Three gates and a dimension, each from a reproduced review finding:
   *
   *  - hasExplanationContent: the drawer fires the "explanation unavailable"
   *    state for an answered question with nothing to show (isAnswered
   *    deliberately includes the server-grade-failure branch). Counting that as
   *    a read made the content-quality metric read BEST exactly where content is
   *    worst — the proxy inverted.
   *  - isUserLoggedIn: on the QLink acquisition path the Explanation tab renders
   *    a sign-in wall. That is a paywall impression, not a read, and QLink is
   *    high volume.
   *  - autoOpened: the drawer opens BY ITSELF on answer in tutor mode (the
   *    default study mode) and on mount in review mode, so the event needs no
   *    user choice at all on the dominant path — it would saturate near 100% and
   *    look healthy for a bank nobody reads. Gating it away would throw out the
   *    tutor-mode population entirely, so instead the cohort travels WITH the
   *    event and can be split out. Without this field the mix is unsalvageable
   *    after the fact.
   */
  // Reported by the container from the ACTUAL open site. Deriving it from mode
  // here made it unconditionally true — isAnswered implies isReview or isTutor —
  // so `autoOpened: false` was unreachable and the dimension split nothing. The
  // fallback keeps the old behaviour if a caller omits the prop, which is
  // conservative: it labels an unknown open as automatic rather than claiming a
  // deliberate read.
  const explanationAutoOpened =
    typeof drawerAutoOpened === "boolean" ? drawerAutoOpened : !!(isTutor || isReview);
  useEffect(() => {
    // hasAnswered, not isAnswered: the container passes a signal that also
    // covers the tutor path, without broadening isAnswered for its two other
    // consumers. Falls back so callers that do not pass it behave as before.
    const answered = typeof hasAnswered === "boolean" ? hasAnswered : isAnswered;
    if (!openDrawer || !answered || !isUserLoggedIn) return;
    if (selectedTab !== "explanation") return;
    if (!hasExplanationContent) return;
    /*
     * ...and hasExplanationContent must describe THIS question.
     *
     * It is computed from `guideData`, which an effect copies from
     * `questionData` — so on a question change this effect runs with the
     * PREVIOUS question's content and the NEW question's id. Review mode keeps
     * the drawer open across questions and the container re-renders rather than
     * remounting, so this is the normal paging path, not an edge case.
     *
     * Measured at the production seam: paging from a question WITH a brief
     * explanation to one with no explanation content at all emitted
     * explanation_opened for the second question. The dedupe latch then makes it
     * permanent. Across a 40-question block essentially every content-less
     * question following a content-bearing one is counted as read — so the
     * metric reads BEST exactly where the content is WORST, which is the
     * inversion the gate above was added to prevent.
     */
    const guideId = guideData?.id ?? guideData?.questionId ?? null;
    const currentId = questionData?.id ?? questionData?.questionId ?? null;
    if (guideId !== currentId) return;
    trackExplanationOpened({
      questionId: questionData?.id ?? questionData?.questionId,
      surface: "exam_review_drawer",
      autoOpened: explanationAutoOpened,
      // The curiosity-vs-repair cut the event exists for. Only passed when
      // actually known — an absent field beats a guessed one.
      // Prefer what the container computed: on the tutor path quizHistory is
      // never populated (it is set only under isReview), so reading it here made
      // the curiosity-vs-repair cut structurally unavailable in the mode the PR
      // calls dominant, while the container was holding the server grade.
      answeredCorrectly:
        typeof answeredCorrectlyProp === "boolean"
          ? answeredCorrectlyProp
          : (typeof quizHistory?.correct === "boolean" ? quizHistory.correct : undefined),
    });
    // First-party funnel: a signed-in QLink viewer read the explanation (the
    // same gates as explanation_opened above; the effect is already signed-in).
    if (isUserAcquisition && qlinkId) {
      recordDrawerFunnel(QLINK_FUNNEL_EVENT.EXPLANATION_VIEWED_AFTER_SIGNIN, {
        mode: explanationAutoOpened ? 'auto' : 'deliberate',
        signin: signinDimension(),
        browser: inAppFunnelProps(inAppBrowser).browser,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openDrawer,
    isAnswered,
    isUserLoggedIn,
    selectedTab,
    hasExplanationContent,
    explanationAutoOpened,
    answeredCorrectlyProp,
    questionData,
    quizHistory,
    // guideData, so the effect re-runs once the copy-from-questionData effect
    // has caught up. Without it the id guard above is a one-way filter: it
    // correctly refuses the lagging render, and then nothing ever re-checks, so
    // a question that DOES have an explanation is never reported either. The
    // guard needs a second chance to say yes, not just a first chance to say no.
    guideData,
    hasAnswered,
  ]);

  const handleExplanationRetry = () => {
    if (typeof onRetryExplanation === 'function') {
      onRetryExplanation();
    } else {
      handleFetchQuestionMetadata();
    }
  };

  const examPracticePrefix = qlinkExamLabel ? qlinkExamLabel + ' ' : '';

  return (
    <>
      <Snackbar open={loginFailed} onClose={() => setLoginFailed(false)} autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <>
          <Box className="success-warn">
            Sign-in could not open. Allow popups and try again.
          </Box>
        </>
      </Snackbar>
      {isPdf &&
        <Box component="div" m={1} ref={pageRef}>
          <Button
            className="close-btn"
            onClick={closePdf}
          >
            X
          </Button>
          <Suspense fallback={<CircularProgress size={24} />}>
            <DrawerPdfViewer
              numPages={numPages}
              pageNumber={pageNumber}
              pageProps={pageProps}
              onLoadSuccess={onDocumentLoadSuccess}
            />
          </Suspense>
        </Box>
      }
      {!isPdf &&
        <Box component="div" className="drawer-cont" {...(teaserResult && !openDrawer ? { inert: "" } : {})}>
          {!teaserResult && <Box className={`${(quizHistory?.correct || isUserAcquisitionCorrectAns) ? 'correct-ans' : quizHistory?.correct === false || isUserAcquisitionWrongAns ? 'wrong-ans' : ''} exp-header`}>
            <Box className="header-lbls">
              <Box component="h2" className="detailed-guidance-lbl">{isUserAcquisition && !isUserLoggedIn ? (selectedAnswer ? 'Your result' : 'Answer to see your result') : 'Detailed Guidance'}</Box>
              {!isTutorRole && !isUserAcquisition &&
                <Box className="icons-wrap">
                  {quizHistory?.correct && <><Check className="correct-icon"></Check> Correct</>}
                  {quizHistory?.correct === false && <><Clear className="incorrect-icon"></Clear>Incorrect</>}
                </Box>
              }
            </Box>
            {(isTutorRole && !isGuide && !readOnly) &&
              <IconButton
                onClick={() => onGuidePopOut()}
                title="Pop Out"
                className="pop-out-icon"
              >
                <Launch />
              </IconButton>
            }
          </Box>}
          {!(isUserAcquisition && !isUserLoggedIn) && <Tabs
            value={selectedTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleChange}
            className={`${(quizHistory?.correct || isUserAcquisitionCorrectAns) ? 'correct-ans' : quizHistory?.correct === false || isUserAcquisitionWrongAns ? 'wrong-ans' : ''} review-tab`}
          >
            <Tab className={`${isDarkTheme ? 'drawer-cont-tab-lbl' : ''}`} label="Explanation" value="explanation" />
            {!isUserAcquisition && <Tab className={`${isDarkTheme ? 'drawer-cont-tab-lbl' : ''}`} label="Info" value="info" />}
            {!isUserAcquisition && !readOnly && <Tab className={`${isDarkTheme ? 'drawer-cont-tab-lbl' : ''}`} label="Feedback" value="feedback" />}
          </Tabs>}

          {selectedTab === "explanation" && (
            <>
              {/* data-hj-suppress on every drawer pane: Hotjar replays the DOM, so an
              unsuppressed pane puts the licensed explanation text into a session
              recording verbatim. Stamped on the PANE, not inside it — the QIR B3 I1
              parity snapshot pins this element's innerHTML, which an attribute on the
              element itself leaves untouched, and suppression is inherited by every
              descendant regardless. Unlike the GL route guard in public/index.html it
              also holds when the surface is reached by SPA client-nav. Clicks, scroll
              depth and rage-clicks still record; the text does not. */}
              {(isUserLoggedIn) &&
                <Box p={3} className="tabs-cont" {...REPLAY_MASK} aria-busy={showQuestionMetadataLoading}>
                  {/* The next step leads, above the explanation: below a long
                      explanation it was several screens down on a phone, and
                      the only action in view was the footer's red Return. */}
                  {showStartPracticing && (
                    <Box className="qlink-next-step">
                      <Button
                        variant="contained"
                        color="primary"
                        className="qlink-start-practicing-btn"
                        onClick={onStartPracticing}
                        fullWidth
                        style={{ textTransform: 'none' }}
                      >
                        {practiceLabel}
                      </Button>
                    </Box>
                  )}
                  {showQuestionMetadataLoading ? (
                    <Box className="explanation-loading-state" role="status" aria-live="polite">
                      <CircularProgress size={30} className="explanation-loading-spinner" />
                      <Typography className="explanation-loading-title">Loading explanation...</Typography>
                      <Typography className="explanation-loading-copy">
                        Getting the detailed guidance for this question.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {openDrawer && guideData?.videoLinks?.length > 0 && (
                        <iframe title={guideData?.id} allow='autoplay;fullscreen' id="video" width="420" height="345"
                          src={convertUrl(guideData?.videoLinks[0])}>
                        </iframe>
                      )}
                      {/* QIR B3 (I1): the correct-answer banner + explanation prose +
                          key-points box + additional-explanation section are now the
                          SHARED DetailedGuidanceContent renderer, consumed identically by
                          the review-queue mount. The drawer keeps supplying its own
                          quiz-derived props; `showFullExplanation={isAdmin}` reproduces
                          the prior `isAdmin && <Box>` gate EXACTLY, so this student mount's
                          output is byte/DOM-unchanged (pinned by the T-B3-1 parity snapshot). */}
                      <DetailedGuidanceContent
                        correctAnswerText={guideData?.option?.correctAnswer}
                        explanation={guideData?.explanation}
                        briefExplanation={guideData?.briefExplanation}
                        additionalExplanation={guideData?.additionalExplanation}
                        showFullExplanation={isAdmin}
                        refLinks={(typeof hasAnswered === "boolean" ? hasAnswered : isAnswered) ? guideData?.refLinks : undefined}
                      />

                      {/* R3: one explicit unavailable/error state — covers a
                          metadata fetch error, a failed A2 grade, AND an empty
                          reveal on an answered question. Never a silent blank.
                          Reuses the loading-state block styling. */}
                      {showExplanationUnavailable && (
                        <Box className="explanation-loading-state explanation-unavailable-state" role="status" aria-live="polite">
                          <Typography className="explanation-loading-title" color="error">
                            We couldn't load the explanation
                          </Typography>
                          <Typography className="explanation-loading-copy">
                            {questionMetadataError
                              ? questionMetadataError
                              : "The detailed guidance for this question isn't available right now."}
                            {explanationErrorCode ? ` (error ${explanationErrorCode})` : ''}
                          </Typography>
                          <Button
                            variant="text"
                            color="primary"
                            className="explanation-retry-btn"
                            onClick={handleExplanationRetry}
                            style={{ textTransform: 'none' }}
                          >
                            Retry
                          </Button>
                        </Box>
                      )}

                      {/* QLink same-case guidance: one status-picked actionable
                          state — never a silent blank, never Retry against a
                          dead login (contract §5 UI / §6.6). */}
                      {showQlinkSignInAgain && (
                        <Box className="explanation-loading-state explanation-unavailable-state qlink-guidance-state" role="status" aria-live="polite">
                          <Typography className="explanation-loading-title" color="error">
                            Please sign in again to see your explanation
                          </Typography>
                          <Typography className="explanation-loading-copy">
                            Your sign-in has expired. Sign in again to unlock the explanation for this exact question.
                          </Typography>
                          <Button
                            variant="outlined"
                            color="primary"
                            className="qlink-signin-again-btn"
                            onClick={onGoogleSignUp}
                            style={{ textTransform: 'none' }}
                          >
                            Sign in again
                          </Button>
                        </Box>
                      )}
                      {showQlinkUnavailable && (
                        <Box className="explanation-loading-state explanation-unavailable-state qlink-guidance-state" role="status" aria-live="polite">
                          <Typography className="explanation-loading-title" color="error">
                            We couldn't open this shared question
                          </Typography>
                          {/* Deliberately non-definitive: this state also
                              covers a missing local share token and a backend
                              not yet serving the route — never claim expiry
                              or revocation as fact. */}
                          <Typography className="explanation-loading-copy">
                            Reopen the original link you were sent to try again, or use Start practicing above to build your own quiz.
                          </Typography>
                        </Box>
                      )}
                      {showQlinkRetry && (
                        <Box className="explanation-loading-state explanation-unavailable-state qlink-guidance-state" role="status" aria-live="polite">
                          <Typography className="explanation-loading-title" color="error">
                            We couldn't load the explanation
                          </Typography>
                          <Typography className="explanation-loading-copy">
                            Something went wrong while loading the guidance for this question.
                          </Typography>
                          <Button
                            variant="text"
                            color="primary"
                            className="explanation-retry-btn"
                            onClick={handleExplanationRetry}
                            style={{ textTransform: 'none' }}
                          >
                            Retry
                          </Button>
                        </Box>
                      )}
                      {showQlinkEmptyGuidance && (
                        <Box className="explanation-loading-state explanation-unavailable-state" role="status" aria-live="polite">
                          <Typography className="explanation-loading-title" color="error">
                            We couldn't load the explanation
                          </Typography>
                          <Typography className="explanation-loading-copy">
                            The guidance for this question didn't arrive. Please retry.
                          </Typography>
                          <Button
                            variant="text"
                            color="primary"
                            className="explanation-retry-btn"
                            onClick={handleExplanationRetry}
                            style={{ textTransform: 'none' }}
                          >
                            Retry
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              }

              {(isUserAcquisition && !isUserLoggedIn) && teaserResult &&
                <QLinkSigninTeaser
                  result={teaserResult}
                  examLabel={qlinkExamLabel}
                  isOpen={!!openDrawer}
                  modal={!!teaserModal}
                  moveFocusOnOpen={teaserMoveFocus !== false}
                  questionKey={funnelQuestionId ?? ''}
                  qlinkId={isUserAcquisition ? qlinkId : undefined}
                  questionId={funnelQuestionId}
                  platform={qlinkPlatform}
                  authMode={effectiveAuthMethod}
                  showGoogleButton={!showInAppEscape}
                  authPanel={showInAppEscape ? (
                    <InAppEscapePanel
                      inAppBrowser={inAppBrowser}
                      platform={qlinkPlatform || 'unknown'}
                      qlinkId={isUserAcquisition ? qlinkId : undefined}
                      questionId={funnelQuestionId}
                      onTryGoogleAnyway={() => {
                        trackTeaserAuthStart('google', qlinkPlatform, teaserKind);
                        onGoogleSignUp('inapp_escape');
                      }}
                    />
                  ) : effectiveAuthMethod === 'phone' ? (
                    <Box className="user-acq-cont qlt-phone-wrap">
                      <Box className="user-acq-login-box">
                        <PhoneAuthSection
                          stayOnPage
                          qlinkId={isUserAcquisition ? qlinkId : undefined}
                          qlinkPlatform={qlinkPlatform}
                          qlinkQuestionId={funnelQuestionId}
                          qlinkSurface="teaser"
                        />
                      </Box>
                    </Box>
                  ) : null}
                  onGoogle={() => onGoogleSignUp('teaser')}
                  onUsePhone={() => {
                    setAuthTabPicked(true);
                    setAuthMethod('phone');
                  }}
                  onUseGoogle={() => {
                    setAuthTabPicked(true);
                    setAuthMethod('google');
                  }}
                  onBack={onBackToQuestion}
                />
              }
              {(isUserAcquisition && !isUserLoggedIn) && !teaserResult &&
                <Box className="user-acq-cont">
                  {/* Value before the wall: the verdict for the answer just given,
                      then an honest text teaser (was a 568 KB blurred image of an
                      unrelated explanation). The "why" stays behind sign-in. */}
                  <Box className="qlink-public-result" role="status" aria-live="polite">
                    {publicAnswerResult?.status === 'pending' && (
                      <Typography className="qlink-public-result-line">Checking your answer…</Typography>
                    )}
                    {signedOutResult?.correct === true && (
                      <Typography className="qlink-public-result-line qlink-public-correct">
                        <Check className="correct-icon" /> Correct!
                      </Typography>
                    )}
                    {signedOutResult?.correct === false && (
                      <>
                        <Typography className="qlink-public-result-line qlink-public-wrong">
                          <Clear className="incorrect-icon" /> Not quite.
                        </Typography>
                        <Typography className="qlink-public-result-answer">
                          The correct answer is {signedOutResult.correctLetter || 'shown above'}.{' '}
                          <strong>{signedOutResult.correctAnswer}</strong>
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Typography className="qlink-teaser">
                    {qlinkExamLabel === 'ABSITE'
                      ? `${signInLead} this question.`
                      : `${signInLead} this question, plus free daily ${examPracticePrefix}practice questions.`}
                  </Typography>
                  {qlinkExamLabel === 'ABSITE' && (
                    <Typography className="qlink-free-offer">
                      <strong>{`${FREE_DAILY_ABSITE_QUESTIONS} free ABSITE questions per day.`}</strong> No credit card required.
                    </Typography>
                  )}
                  <Typography className="qlink-auth-reassurance">
                    You’ll stay on this question after signing in.
                  </Typography>

                  <Box className="user-acq-login-box">
                    <Tabs
                      value={effectiveAuthMethod}
                      onChange={(e, newValue) => {
                        setAuthTabPicked(true);
                        setAuthMethod(newValue);
                      }}
                      centered
                      indicatorColor="primary"
                      textColor="primary"
                      style={{ marginBottom: '12px', width: '100%' }}
                    >
                      <Tab label="Google Sign In" value="google" />
                      <Tab label="Phone Sign In" value="phone" />
                    </Tabs>

                    {showInAppEscape && (
                      <InAppEscapePanel
                        inAppBrowser={inAppBrowser}
                        platform={qlinkPlatform || 'unknown'}
                        qlinkId={isUserAcquisition ? qlinkId : undefined}
                        questionId={funnelQuestionId}
                        onTryGoogleAnyway={() => onGoogleSignUp('inapp_escape')}
                      />
                    )}

                    {effectiveAuthMethod === 'google' && !showInAppEscape && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        className="google-btn"
                        id="google_login_btn"
                        onClick={() => onGoogleSignUp('wall')}
                      >
                        <img alt="google" src={googleLogo} width='30' height='30' className="logo" />
                        Sign In with Google
                      </Button>
                    )}

                    {effectiveAuthMethod === 'phone' && (
                      // Stay on this question after sign-in (like the Google
                      // popup) and label the session with this link.
                      <PhoneAuthSection
                        stayOnPage
                        qlinkId={isUserAcquisition ? qlinkId : undefined}
                        qlinkPlatform={qlinkPlatform}
                        qlinkQuestionId={funnelQuestionId}
                        qlinkSurface="wall"
                      />
                    )}
                  </Box>
                </Box>
              }
            </>
          )}

          {selectedTab === "info" && (
            <Box p={3} className="tabs-cont" {...REPLAY_MASK}>
              <Box component="div" className="info-list-items">
                <Typography className="label">Discipline</Typography>
                <Typography className="item-value">
                  {guideData?.categories?.discipline?.join(", ")}
                </Typography>
              </Box>
              <Box component="div" className="info-list-items">
                <Typography className="label"> Organ System </Typography>
                <Typography className="item-value">
                  {guideData?.categories?.organSystem?.join(", ")}
                </Typography>
              </Box>
              <Box component="div" className="info-list-items">
                <Typography className="label"> Exam Type </Typography>
                <Typography className="item-value">
                  {guideData?.categories?.examType?.join(", ")}
                </Typography>
              </Box>
              <Box component="div" className="info-list-items">
                <Typography className="label"> Topics </Typography>
                <Typography className="item-value">
                  {guideData?.medicalCategories?.topics?.join(", ")}
                </Typography>
              </Box>
              <Box component="div" className="info-list-items">
                <Typography className="label"> Subtopics </Typography>
                <Typography className="item-value">
                  {guideData?.medicalCategories?.subtopics?.join(", ")}
                </Typography>
              </Box>
              {isAdmin && (guideData?.userEmail || authorEmail) &&
                <Box component="div" className="info-list-items">
                  <Typography className="label"> Author </Typography>
                  <Typography className="item-value">
                    {guideData?.userEmail || authorEmail}
                  </Typography>
                </Box>
              }
              {!isTutor &&
                <>
                  <Box component="div" className="info-list-items">
                    <Typography className="label"> Time Spent Testing </Typography>
                    <Typography className="item-value">
                      {Math.floor(((quizHistory?.timeSpent || 0) / 60) % 60) < 10 && 0}
                      {Math.floor(((quizHistory?.timeSpent || 0) / 60) % 60)}:
                      {Math.floor((quizHistory?.timeSpent || 0) % 60) < 10 && 0}
                      {Math.floor((quizHistory?.timeSpent || 0) % 60)}
                    </Typography>
                  </Box>
                  <Box component="div" className="info-list-items">
                    <Typography className="label"> Time Spent Reviewing </Typography>
                    <Typography className="item-value">
                      {Math.floor(((quizHistory?.reviewTimeSpent || 0) / 60) % 60) < 10 && 0}
                      {Math.floor(((quizHistory?.reviewTimeSpent || 0) / 60) % 60)}:
                      {Math.floor((quizHistory?.reviewTimeSpent || 0) % 60) < 10 && 0}
                      {Math.floor((quizHistory?.reviewTimeSpent || 0) % 60)}
                    </Typography>
                  </Box>
                </>
              }
            </Box>
          )}

          {!readOnly && selectedTab === "feedback" &&
            <>
              {!isTutorRole && !isAdmin &&
                <Box p={3} className="tabs-cont" {...REPLAY_MASK}>
                  {quesFeedbackArr?.length > 0 && quesFeedbackArr?.map((item, index) =>
                    <Box component="div" className="info-list-items feedback" key={index}>
                      <Box className="label1">{item?.user?.email}</Box>
                      <Box className="item-row">
                        <Typography className="item-value">
                          Error Suspected: {item?.isError === 'Y' ? 'Yes' : 'No'}
                        </Typography>
                        {item?.rating > 0 &&
                          <Typography className="item-value">
                            Quality Rating: {item?.rating}
                          </Typography>
                        }
                        {item?.topic &&
                          <Typography className="item-value">
                            Topic: {getTransformedText(item?.topic)}
                          </Typography>
                        }
                        {item?.explanationType &&
                          <Typography className="item-value">
                            Error location: {getTransformedText(item?.explanationType)}
                          </Typography>
                        }
                      </Box>

                      {item?.comment &&
                        <Typography className="item-value">
                          {item?.comment}
                        </Typography>
                      }
                    </Box>
                  )}
                  {!quesFeedbackArr?.length &&
                    <Box className="no-feedback-cont">
                      <Typography className="item-value">No feedback yet.
                      </Typography>
                      {!isUserAcquisition &&
                        <IconButton
                          className="quesfeedback-btn"
                          onClick={() => onFeedbackOpen(true)}
                          title="Question Feedback"
                        >
                          <Forum />
                        </IconButton>
                      }
                    </Box>
                  }
                </Box>
              }
              {isAdmin &&
                <Box p={3} className="tabs-cont" {...REPLAY_MASK}>
                  {quesTutorFeedbackArr?.length > 0 && quesTutorFeedbackArr.map((item, index) =>
                    <Box component="div" className="info-list-items feedback" key={index}>
                      <Box className="label1">From: {item?.user?.email}</Box>
                      <Box className="item-row-box">
                        <Box className="label-box">STEM</Box>
                        <Typography className="item-value-box">
                          Quality: {getTransformedText(item?.stem.quality)}.
                          Difficulty: {getTransformedText(item?.stem.difficulty)}.
                          Errors: {getTransformedText(item?.stem.errors)}.
                        </Typography>
                        <Box className="label-box">EXPLANATION</Box>
                        <Typography className="item-value-box">
                          Quality: {getTransformedText(item?.explanation.quality)}.
                          Errors: {getTransformedText(item?.explanation.errors)}.
                        </Typography>
                        {(item?.improvementSuggestions?.stem ||
                          item?.improvementSuggestions?.explanation ||
                          item?.improvementSuggestions?.expandedExplanation)
                          && <>
                            <Box className="label-box">Improvement Suggestions</Box>
                            {item?.improvementSuggestions?.stem &&
                              <Typography component='span' className="item-value-box">
                                Stem: {getTransformedText(item?.improvementSuggestions?.stem)}, &nbsp;
                              </Typography>
                            }
                            {item?.improvementSuggestions?.explanation &&
                              <Typography component='span' className="item-value-box">
                                Explanation: {getTransformedText(item?.improvementSuggestions?.explanation)}, &nbsp;
                              </Typography>
                            }

                            {item?.improvementSuggestions?.expandedExplanation &&
                              <Typography component='span' className="item-value-box">
                                Expanded Explanation: {getTransformedText(item?.improvementSuggestions?.expandedExplanation)}.
                              </Typography>
                            }
                          </>
                        }
                      </Box>
                    </Box>
                  )}
                  {!quesTutorFeedbackArr?.length &&
                    <Box className="no-feedback-cont">
                      <Typography className="item-value">No feedback yet
                      </Typography>

                      {/* <IconButton
                        className="quesfeedback-btn"
                        onClick={() => onFeedbackOpen(true, guideData?.id)}
                        title="Question Feedback"
                      >
                        <Forum />
                      </IconButton> */}
                    </Box>
                  }
                </Box>
              }
              {
                isTutorRole &&
                <QuestionFeedback
                  className="ques-feedbk-modal"
                  questionData={questionData}
                  isGuide={isGuide}
                  currentQuestion={currentQuestionNum} />
              }
            </>
          }
        </Box>
      }
    </>
  );
};

export default ExamReviewDrawer;
